import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import { observer } from 'mobx-react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AuthStore from '../../store/AuthStore';
import LeadStore from '../../store/LeadStore';
import { DatePicker, DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { enIN } from 'date-fns/locale';
import dayjs from 'dayjs';
import { Close } from '@mui/icons-material';
import { CustomStyledButton, CustomStyledDialog } from '../../utils/helper';
import { datetimeFormatter } from '../../utils/datetimeFormatter';

export const NewLeadCallDialog = observer(
  ({ leadDetails, leadCallDetails, showNewLeadCallDialog, handleShowNewLeadCallDialog }) => {
    const emptyFormData = {
      id: leadCallDetails?.id ?? 0,
      name: leadCallDetails?.name ?? '',
      description: leadCallDetails?.description ?? '',
      callReminderDate: leadCallDetails?.callReminderDate
        ? datetimeFormatter.toLocalTime(leadCallDetails?.callReminderDate)
        : '',
      callStatus: leadCallDetails?.callStatus ?? '',
      leadId: leadDetails.id,
      userId: AuthStore.userDetails.Id,
      userName: AuthStore.userDetails.Name,
      createdDate: new Date(),
    };

    const [formData, setFormData] = useState(emptyFormData);

    useEffect(() => {
      setFormData(emptyFormData);
    }, [showNewLeadCallDialog]);

    const [errors, setErrors] = useState({
      name: false,
      description: false,
      callReminderDate: false,
    });

    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData({ ...formData, [name]: value });

      // Perform validation
      setErrors({ ...errors, [name]: isEmpty(value) });
    };

    const handleSubmit = (e) => {
      e.preventDefault();

      const updatedErrors = {
        name: isEmpty(formData.name),
        description: isEmpty(formData.description),
        callReminderDate: isEmpty(formData.callReminderDate),
      };
      setErrors(updatedErrors);

      if (!Object.values(updatedErrors).some((error) => error === true)) {
        LeadStore.createCallLogs(formData);
      }
    };

    useEffect(() => {
      if (LeadStore.createLeadCallStatus) {
        handleShowNewLeadCallDialog();
        LeadStore.getDetailedLead(leadDetails.id);
      }
    }, [LeadStore.createLeadCallStatus]);

    const isEmpty = (value) => {
      return (
        value === null ||
        value === undefined ||
        (!Number.isInteger(value) && !(value instanceof Date) && value?.trim() === '')
      );
    };

    return (
      <CustomStyledDialog
        open={showNewLeadCallDialog}
        onClose={(event, reason) => reason !== 'backdropClick' && handleShowNewLeadCallDialog()}
        fullWidth={true}
        maxWidth={'xs'}
        noValidate
        PaperProps={{
          component: 'form',
          noValidate: true,
          onSubmit: (e) => handleSubmit(e),
        }}
      >
        <DialogTitle>
          <Stack textAlign={'center'}>
            <Typography sx={{ fontWeight: '700' }} component="p">
              {formData.id === 0 ? 'New' : 'Update'} Call Remainder
            </Typography>
          </Stack>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleShowNewLeadCallDialog}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
        <DialogContent>
          <Stack direction={'column'} spacing={2} sx={{ py: '10px' }}>
            <TextField
              id="outlined-name"
              name="name"
              label="Name"
              size="small"
              value={formData.name}
              onChange={(e) => handleChange(e)}
              error={Boolean(errors.name)}
              fullWidth
              required
            />
            <TextField
              id="outlined-description"
              name="description"
              label="Description"
              size="small"
              value={formData.description}
              onChange={handleChange}
              error={Boolean(errors.description)}
              fullWidth
              multiline
              rows={2}
              required
            />
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
              <DateTimePicker
                label="Start Date"
                required
                value={formData.callReminderDate ? new Date(formData.callReminderDate) : null}
                onChange={(newValue) => {
                  handleChange({
                    target: { name: 'callReminderDate', value: newValue },
                  });
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    required: true,
                    error: errors.callReminderDate,
                  },
                }}
              />
            </LocalizationProvider>
            <TextField
              id="outlined-callStatus"
              name="callStatus"
              label="callStatus"
              size="small"
              value={formData.callStatus}
              onChange={(e) => handleChange(e)}
              error={Boolean(errors.callStatus)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: '25px', pt: '0px', justifyContent: 'center' }}>
          <CustomStyledButton variant="contained" type="submit" autoFocus>
            Save
          </CustomStyledButton>
        </DialogActions>
      </CustomStyledDialog>
    );
  }
);
